import { Grid, Link, ListItem, ListItemButton, styled, Stack, Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { isEmpty, capitalize } from 'lodash-es';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { IComposableDashboardCollectionsContext, IComposableDashboardContext } from 'wisestep-api';
import {
  WsColors,
  WsIconMoreVer,
  WsTypoColors,
  WsTypoVariants,
  WsTypography,
  WsDataGrid,
  getDateUsingFormat,
  SearchBarNew,
  WsPageHeader,
  PopoverOnClick,
  WsBadge,
  WsBadgeSize,
  WsBadgetype,
} from 'wisestep-base';

import Loader from '../../../features/conversationWidget/components/Loader';
import {
  ComposableAnalyticsDataAccessEnum,
  useComposableAnalyticsDataAccessHook,
} from '../../../hooks/useComposableAnalyticsDataAccessHook';
import NoDashboards from '../analyticsOptions/NoDashboards';
import { useComposableDashboardCollectionsContext } from '../hooks/useComposableDashboardCollectionsContext';
import { useComposableDashboardsContext } from '../hooks/useComposableDashboardsContext';
import { IAnalyticsMenuOption } from '../types';

export const AnalyticsMenuItem = styled(ListItem)(({ theme }) => ({
  cursor: 'pointer',
  height: '100%',
  minHeight: 80,
  border: '2px solid' + WsColors.BorderGrey,
  borderRadius: theme.spacing(0.5),
  ':hover': {
    boxShadow: '1px 1px 5px' + WsColors.BorderGrey,
  },
}));

export interface IAnalyticsContentLayoutProps {
  options: IAnalyticsMenuOption[];
  title: string;
  headerButton?: ReactElement;
  selectedDashboardId?: 'master' | 'dashboard';
  access?: boolean;
}

const AnalyticsContentLayout = ({
  options,
  title,
  selectedDashboardId,
}: IAnalyticsContentLayoutProps) => {
  const isMasterDashboard = selectedDashboardId === 'master';
  const [filteredOptions, setFilteredOptions] = useState<IAnalyticsMenuOption[]>([]);
  const [pageSize, setPageSize] = useState<number>(50);
  const [page, setPage] = useState<number>(0);
  const formMethods = useForm();
  const navigate = useNavigate();
  const { setSelectedDashboardByIndex, dashboards, loader } =
    useComposableDashboardsContext() as Required<IComposableDashboardContext>;

  const { setSelectedCollectionByIndex, dashboardCollections } =
    useComposableDashboardCollectionsContext() as Required<IComposableDashboardCollectionsContext>;

  const { access: hasDataAccess, owns: isAdmin } = useComposableAnalyticsDataAccessHook([
    ComposableAnalyticsDataAccessEnum.CREATE_DASHBOARDS,
    ComposableAnalyticsDataAccessEnum.CREATE_MASTER_DASHBOARDS,
  ]);
  const onNavigate = (index: number, option: IAnalyticsMenuOption) => {
    if (!isEmpty(option.url)) {
      selectedDashboardId == 'master' && setSelectedCollectionByIndex(index, true);
      selectedDashboardId == 'dashboard' && setSelectedDashboardByIndex(index);
      navigate(option.url);
    }
  };

  const dashboardTitles = ['Your Dashboards', 'Master Dashboards'];
  const dataAccess =
    (selectedDashboardId == 'master' ? hasDataAccess?.[1] : hasDataAccess?.[0]) || isAdmin;

  const onSearch = useCallback(
    (data: any) => {
      const searchValue: string = (data.name ?? '').toLowerCase();
      const filteredData = options.filter((item) => item.label.toLowerCase().includes(searchValue));
      setFilteredOptions(filteredData);
    },
    [options]
  );

  useEffect(() => onSearch(formMethods.getValues()), [formMethods, onSearch]);

  const columns: GridColDef[] = [
    {
      field: 'label',
      headerName: 'Dashboard Name',
      flex: 1.5,
      renderCell: (params) => (
        <Link
          underline="hover"
          onClick={() => {
            params.row?.openInNewTab
              ? window.open(params.row.url, '_blank')
              : onNavigate(params.row.state.index, params.row);
          }}
        >
          <WsTypography
            cursor="pointer"
            variant={WsTypoVariants.SUB_TITLE}
            color={WsTypoColors.PRIMARY750}
          >
            {params.value}
          </WsTypography>
        </Link>
      ),
    },
    {
      field: 'tags',
      headerName: 'Tags',
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return (
          <Stack direction="row" spacing={1}>
            <WsBadge
              label={
                params.row.accessLevel == 'restricted'
                  ? 'Private'
                  : capitalize(params.row.accessLevel)
              }
              badgeSize={WsBadgeSize.LARGE}
              badgeType={WsBadgetype.SUCCESS}
            />
            {params.row.isAssigned && (
              <WsBadge
                label="Assigned"
                badgeSize={WsBadgeSize.LARGE}
                badgeType={WsBadgetype.SUCCESS}
              />
            )}
          </Stack>
        );
      },
    },
    ...(selectedDashboardId == 'dashboard'
      ? [
          {
            field: 'description',
            headerName: 'Description',
            flex: 1,
            renderCell: (params) => (
              <WsTypography variant={WsTypoVariants.BODY1}>{params.value}</WsTypography>
            ),
          },
        ]
      : []),
    {
      field: 'createdByName',
      headerName: 'Created By',
      flex: 1,
      renderCell: (params) => (
        <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
          {params.value}
        </WsTypography>
      ),
    },
    {
      field: 'createdOn',
      headerName: 'Date Created',
      flex: 1,
      renderCell: (params) => (
        <WsTypography variant={WsTypoVariants.BODY1}>
          {getDateUsingFormat(params.value, 'DD-MMM-YY')}
        </WsTypography>
      ),
    },
    {
      field: 'action',
      headerName: '',
      width: 50,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
        params.row.showKebabMenu && (
          <PopoverOnClick element={<WsIconMoreVer />}>
            <Stack p={1}>
              {params.row.kebabMenuOptions?.map((item) => (
                <ListItemButton
                  key={item.text}
                  onClick={(event) => {
                    const dashboardIndex = options.findIndex(
                      (option) => option.id == params.row.id
                    );
                    item?.onClick(dashboardIndex);
                    event.stopPropagation();
                  }}
                >
                  <WsTypography
                    variant={WsTypoVariants.BODY1}
                    color={WsTypoColors.PRIMARY}
                    cursor="pointer"
                  >
                    {item.text}
                  </WsTypography>
                </ListItemButton>
              ))}
            </Stack>
          </PopoverOnClick>
        ),
    },
  ];

  const renderDashboardTable = () => (
    <Grid item xs={12}>
      <WsDataGrid
        autoHeight
        rows={filteredOptions}
        columns={columns}
        initialState={{
          sorting: {
            sortModel: [{ field: 'createdOn', sort: 'desc' }],
          },
        }}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </Grid>
  );

  return (
    <Grid container rowGap={2} columnSpacing={2}>
      {dataAccess && dashboardTitles.includes(title) && (
        <Grid item xs={12}>
          <WsPageHeader
            title={isMasterDashboard ? dashboardTitles[1] : dashboardTitles[0]}
            tooltip="View user across different teams"
            buttonTitle={
              isMasterDashboard ? 'Create new Dashboard Collection' : 'Create new Dashboard'
            }
            onButtonClick={() => {
              setSelectedDashboardByIndex(-1);
              isMasterDashboard
                ? navigate('/workspace-users-and-teams/master-dashboards/view')
                : navigate('/workspace-users-and-teams/your-dashboards/view');
            }}
            transparent={true}
          />
        </Grid>
      )}
      <Grid
        item
        xs={5}
        sx={{
          ml: 2.5,
          mb: 1,
        }}
      >
        <FormProvider {...formMethods}>
          <Box
            component="form"
            onSubmit={formMethods.handleSubmit((data) => {
              onSearch(data);
              setPage(0);
            })}
          >
            <SearchBarNew size="small" name="name" type="submit" />
          </Box>
        </FormProvider>
      </Grid>
      {selectedDashboardId == 'dashboard' ? (
        isEmpty(dashboards) && loader ? (
          <Loader />
        ) : isEmpty(dashboards) ? (
          <NoDashboards />
        ) : (
          renderDashboardTable()
        )
      ) : selectedDashboardId == 'master' ? (
        isEmpty(dashboardCollections) && loader ? (
          <Loader />
        ) : isEmpty(dashboardCollections) ? (
          <NoDashboards />
        ) : (
          renderDashboardTable()
        )
      ) : (
        renderDashboardTable()
      )}
    </Grid>
  );
};

export default AnalyticsContentLayout;
